import { createContext, useContext, useReducer, ReactNode } from 'react';
import { TargetCostData } from '@/types/target-cost';

interface TargetCostState {
  data: TargetCostData;
  isDirty: boolean;
}

type TargetCostAction = 
  | { type: 'UPDATE_DATA'; payload: Partial<TargetCostData> }
  | { type: 'CLEAR_DATA' }
  | { type: 'MARK_SAVED' };

// Initial state with proper range structure
const initialTargetCostData: TargetCostData = {
  details: {
    projectName: '',
    clientName: '',
    startDate: null,
    endDate: null,
    reportingPeriodStart: null,
    reportingPeriodEnd: null,
    contractForm: 'NEC4',
  },
  ranges: {
    lessThanRange: {
      type: 'less-than',
      percentage: 90,
      sharePercentage: 50,
    },
    greaterThanRange: {
      type: 'greater-than',
      percentage: 125,
      sharePercentage: 50,
    },
    dynamicRanges: [],
  },
  costs: {
    totalPrices: null,
    finalPWDD: null,
    saving: 0,
    costOverrun: 0,
  },
};

const initialState: TargetCostState = {
  data: initialTargetCostData,
  isDirty: false
};

function targetCostReducer(state: TargetCostState, action: TargetCostAction): TargetCostState {
  switch (action.type) {
    case 'UPDATE_DATA':
      return {
        data: { ...state.data, ...action.payload },
        isDirty: true
      };
    case 'CLEAR_DATA':
      return initialState;
    case 'MARK_SAVED':
      return {
        ...state,
        isDirty: false
      };
    default:
      return state;
  }
}

const TargetCostContext = createContext<{
  state: TargetCostState;
  dispatch: React.Dispatch<TargetCostAction>;
} | null>(null);

export function TargetCostProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(targetCostReducer, initialState);

  return (
    <TargetCostContext.Provider value={{ state, dispatch }}>
      {children}
    </TargetCostContext.Provider>
  );
}

export function useTargetCost() {
  const context = useContext(TargetCostContext);
  if (!context) {
    throw new Error('useTargetCost must be used within TargetCostProvider');
  }
  return context;
}